import { CommonModule } from '@angular/common';
import { Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { finalize } from 'rxjs';
import { AuthService } from '@/core/services/auth.service';
import { ComplianceRequestType } from '@/core/compliance/compliance.model';
import { ComplianceRequestsService } from '@/core/compliance/compliance-requests.service';
import { HelpersService } from '@/shared/services/helpers.service';
import { ButtonComponent } from '@/shared/ui-components/button/button.component';
import { CheckboxComponent } from '@/shared/ui-components/checkbox/checkbox.component';
import { DialogComponent } from '@/shared/ui-components/dialog/dialog.component';

@Component({
    selector: 'app-compliance-requests-dialog',
    standalone: true,
    imports: [CommonModule, FormsModule, TranslateModule, RouterLink, DialogComponent, CheckboxComponent, ButtonComponent],
    templateUrl: './compliance-requests-dialog.component.html'
})
export class ComplianceRequestsDialogComponent {
    private readonly _complianceRequestsService = inject(ComplianceRequestsService);
    private readonly _authService = inject(AuthService);
    private readonly _translateService = inject(TranslateService);
    private readonly _helpersService = inject(HelpersService);

    public readonly pendingRequests = this._complianceRequestsService.pendingRequests;
    public readonly hasPendingRequests = this._complianceRequestsService.hasPendingRequests;
    public readonly acceptedTypes = signal<Set<ComplianceRequestType>>(new Set());
    public readonly submitting = signal(false);

    public readonly allAccepted = computed(() =>
        this.pendingRequests().every((r) => this.acceptedTypes().has(r.type))
    );

    public readonly ComplianceRequestType = ComplianceRequestType;

    public readonly complianceRequestRoutes: Partial<Record<ComplianceRequestType, string>> = {
        [ComplianceRequestType.TERMS_AND_CONDITIONS_ACCEPTANCE]: '/terms-and-conditions',
        [ComplianceRequestType.ACCEPTABLE_USE_POLICY_ACCEPTANCE]: '/acceptable-use-policy',
        [ComplianceRequestType.PRIVACY_POLICY_ACCEPTANCE]: '/privacy-policy',
    };

    public isAccepted(type: ComplianceRequestType): boolean {
        return this.acceptedTypes().has(type);
    }

    public onRequestAcceptedChange(type: ComplianceRequestType, value: boolean | any[]): void {
        const next = new Set(this.acceptedTypes());
        if (value === true) next.add(type);
        else next.delete(type);
        this.acceptedTypes.set(next);
    }

    public submit(): void {
        const requests = this.pendingRequests();
        if (requests.length === 0 || !this.allAccepted()) return;

        this.submitting.set(true);
        const types = requests.map((r) => r.type);
        this._complianceRequestsService.completeAllRequests(types)
            .pipe(finalize(() => this.submitting.set(false)))
            .subscribe(() => {
                this.acceptedTypes.set(new Set());
                this._helpersService.showSuccessMessage(
                    this._translateService.instant('COMPLIANCE.REQUESTS_COMPLETED_SUCCESSFULLY') as string
                );
            });
    }

    public logout(): void {
        this._complianceRequestsService.clear();
        this.acceptedTypes.set(new Set());
        this._authService.clearTokens();
        // Hard reload to reset all singleton services holding previous-user state.
        window.location.href = '/auth/login';
    }
}
